import React, { useState, useEffect, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'

import InvalidForm from 'src/components/InvalidForm'
import Panel from '../Panel'
import Backdrop from '../fiat/Backdrop'
import Processing from '../Processing'
import Form from './Form'
import Payment from './Payment'
import Preview from './Preview'
import I18n from 'src/components/I18n'

import { root } from 'src/utils/mountReactApp'
import csrfHeaders from 'src/utils/csrfHeaders'
import partnerConfig from './partnerConfig'

const App = ({ elements, honoreeName, theFamilyOf, providedAddress, partner, session, stripeKey, ...props }) => {
  const [ donation, setDonation ] = useState(null)
  const [ sendTo, setSendTo ] = useState(providedAddress ? 'provided' : 'custom')
  const [ customAddress, setCustomAddress ] = useState({})
  const [ loading, setLoading ] = useState(false)
  const [ panel, setPanel ] = useState('form')
  const [ error, setError ] = useState(null)
  const [ priceData, setPriceData ] = useState(null)
  const [ test, setTest ] = useState(false)
  const mounted = useRef(false)

  useEffect(() => {
    if (!session.staff) return

    const handleMessage = e => {
      if (e.origin != location.origin) return

      switch (e.data) {
      case 'toggleUpsellTestMode':
        return setTest(test => !test)
      }
    }

    addEventListener('message', handleMessage)
    return () => removeEventListener('message', handleMessage)
  }, [ session.staff ])

  useEffect(() => {
    const handleDonationSet = e => setDonation(e.detail)
    root.addEventListener('donationSet', handleDonationSet)
    return () => root.removeEventListener('donationSet', handleDonationSet)
  }, [])

  useEffect(() => {
    if (donation?.simulated) setTest(true)
  }, [ donation?.simulated ])

  useEffect(() => {
    if (!mounted.current) return

    console.log(`Sympathy card test mode: ${test ? 'ON' : 'OFF'}`)
  }, [ test ])

  useEffect(() => {
    mounted.current = true
    return () => mounted.current = false
  }, [])

  if (!donation) return null

  const i18n = I18n(props.i18n)
  const config = partnerConfig[partner.handle]

  const calculatePrice = async address => {
    setLoading(true)

    try {
      const response = await fetch('/payment_processors/sympathy_cards/calculate_price', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({ address }),
      })
      const data = await response.json()
      if (response.ok) {
        setPriceData(data)
        setPanel('payment')
      } else {
        setError('An error occurred calculating price. Please try again.')
      }
    } catch (error) {
      console.error(error)
      setError('A network error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleFormSubmit = async e => {
    e.preventDefault()
    if (loading) return

    setError(null)

    if (sendTo == 'custom') {
      const { complete } = await elements.getElement('address').getValue()
      if (complete) calculatePrice(customAddress.address)
    } else calculatePrice(providedAddress.address)
  }

  const goBack = () => {
    setPanel('form')
    setPriceData(null)
  }

  const handlePaymentSubmit = async (e) => {
    e.preventDefault()
    if (loading) return

    setError(null)
    setLoading(true)

    try {
      let to_name, address
      if (sendTo == 'custom') {
        to_name = customAddress.name
        address = customAddress.address
      } else {
        to_name = theFamilyOf
        if (config?.showCareOf)
          to_name += `\nc/o ${providedAddress.name}`
        address = providedAddress.address
      }

      // Step 1: Create the upsell order and get an unconfirmed PaymentIntent
      const response = await fetch('/payment_processors/sympathy_cards', {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          to_name,
          address,
          order_id: donation.token,
          decedent_name: honoreeName,
          test: Number(test),
        }),
      })

      if (!response.ok) {
        try {
          const json = await response.json()
          setError(
            json.errors?.[0] ||
            json.message ||
            'An error occurred ordering the sympathy card.'
          )
        } catch (error) {
          console.error(error)
          setError('An unknown error occurred. Please try again.')
        }
        return
      }

      const { client_secret, stripe_account } = await response.json()

      // Step 2: Confirm the PaymentIntent on the connected account (Legacy)
      const connectedStripe = await loadStripe(stripeKey, { stripeAccount: stripe_account, locale: i18n.locale })
      const { error: confirmError } = await connectedStripe.confirmCardPayment(client_secret)

      if (confirmError) {
        setError(confirmError.message || 'Payment failed. Please try again.')
        return
      }

      // Step 3: Finalize the upsell order after successful payment
      const url = `/payment_processors/sympathy_cards/${donation.token}`
      const finalizeResponse = await fetch(url, { method: 'PATCH', headers: csrfHeaders() })

      // Payment succeeded — show success even if finalization failed,
      // since the card was already charged.
      if (!finalizeResponse.ok)
        console.error('Sympathy card finalization failed after successful payment')

      root.dispatchEvent(new Event('cardSent'))
      const modalElement = document.getElementById('sympathyCardModal')
      bootstrap.Modal.getOrCreateInstance(modalElement).hide()
    } catch (error) {
      console.error(error)
      setError('A network error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {
        test &&
        <div className="alert alert-warning">
          <b>Test mode:</b> Sympathy cards ordered in test mode will be marked
          as <code>test</code> in API calls.
        </div>
      }

      <div className="mb-3">
        <Preview donation={donation} honoreeName={honoreeName} />
      </div>

      <InvalidForm>{error}</InvalidForm>

      <Panel.Collection panel={panel} flush>
        <Panel name="form">
          <Form
            handleSubmit={handleFormSubmit}
            providedAddress={providedAddress}
            config={config}
            loading={loading}
            sendTo={sendTo}
            setSendTo={setSendTo}
            customAddress={customAddress}
            setCustomAddress={setCustomAddress}
            theFamilyOf={theFamilyOf}
          />
        </Panel>
        <Panel name="payment">
          <Payment
            handleSubmit={handlePaymentSubmit}
            priceData={priceData}
            goBack={goBack}
            loading={loading}
          />
        </Panel>
      </Panel.Collection>

      <Backdrop show={loading && panel == 'payment'} position="center">
        <Processing>
          <h2 className="h5 text-primary modal-heading modal-heading-processing">
            Purchasing Sympathy Card
          </h2>
          <p className="mb-0">{i18n.t('do_not_hit_back')}</p>
        </Processing>
      </Backdrop>
    </div>
  )
}

export default App
